import theme from 'config/theme';
import { RecurringAmountMobileContainer, RecurringCancelLink, RecurringCancelled } from './styled';
import { TSubscription } from 'store/subscriptions/types';
import { formatNumberAsCurrency, getFormattedDate, isKeyInObject } from 'helpers/utils';
import { BodySmall, UtilityText } from 'components/Typography';
import Spacing from 'components/Spacing';
import { IUserStripeSubscriptionData } from 'store/userStripeSubscriptions/types';
import { getDisplayTextFromRecurringInterval } from 'screens/Checkout/Form/utils';
import { PageNameAndCancel } from './StripeRecurringDonations/PageNameAndCancel';
import { RecommendationsData } from 'store/recommendations/types';

export const FALLBACK_IMPACT_QUERY_STR = 'mx';
export const CURRENT_CYCLE_YEAR = 2026;

export const getImpactQueryStr = (
  recommendations: RecommendationsData[] | undefined,
  profileImpactQueryStr: string | undefined
): string | undefined => profileImpactQueryStr || recommendations?.[0]?.query_str;

export const monthlyTagNameMap = {
  'Monthly Flip the US House Slate': 'Flip the House',
  'Monthly Defeat Trump Slate': 'Defeat Trump',
  'Monthly Repro Slate': 'Reproductive Rights',
  'Monthly Protecting Democracy Slate': 'Protect Democracy',
  'Monthly Democratic Infrastructure Slate': 'Dem Infrastructure',
};

export const getIsSubscriptionActive = (subscription: IUserStripeSubscriptionData) => {
  return subscription.status === 'active' && !subscription.cancel_at_period_end;
};

export const getMonthlyTagName = (tagName: string) => {
  if (!isKeyInObject(tagName, monthlyTagNameMap)) {
    return 'Monthly Slate';
  }
  return monthlyTagNameMap[tagName];
};

const stripeRecurringColumns = {
  page_query_string: {
    key: 'page_query_string',
    name: `Recurring Donation`,
  },
  interval: {
    key: 'interval',
    name: 'Cadence',
    formatFn: (interval: string, subscription: IUserStripeSubscriptionData) => {
      if (subscription.interval_count === 3 && interval === 'month') {
        return 'Quarterly';
      }
      return getDisplayTextFromRecurringInterval(subscription.interval, true);
    },
    className: 'align-left',
  },
  current_period_end: {
    key: 'current_period_end',
    name: 'Next Payment',
    formatFn: (currentPeriodEnd: number | null, subscription: IUserStripeSubscriptionData) =>
      typeof currentPeriodEnd === 'number' && getIsSubscriptionActive(subscription)
        ? getFormattedDate(currentPeriodEnd * 1000)
        : 'N/A',
    className: 'align-left',
  },
  amount: {
    key: 'amount',
    name: 'Donation Amount',
    formatFn: (amount = 0) => formatNumberAsCurrency(amount / 100, { trailingZeroDisplay: 'auto' }),
  },
};

export const getColumnsStripeRecurring = () => [
  {
    ...stripeRecurringColumns.page_query_string,
    width: '35%',
    formatFn: (pageQueryString: string, subscription: IUserStripeSubscriptionData) => (
      <PageNameAndCancel
        pageQueryString={pageQueryString}
        subscription={subscription}
        isMobile={false}
      />
    ),
  },
  stripeRecurringColumns.interval,
  {
    ...stripeRecurringColumns.current_period_end,
    width: '20%',
  },
  {
    ...stripeRecurringColumns.amount,
    width: '25%',
  },
];

export const getColumnsStripeRecurringMobile = () => [
  {
    ...stripeRecurringColumns.page_query_string,
    width: '50%',
    formatFn: (pageQueryString: string, subscription: IUserStripeSubscriptionData) => (
      <PageNameAndCancel
        pageQueryString={pageQueryString}
        subscription={subscription}
        isMobile={true}
      />
    ),
  },
  stripeRecurringColumns.interval,
  {
    ...stripeRecurringColumns.amount,
    name: 'Amount',
    width: '20%',
  },
];

export const getColumnsMonthly = (
  getDeleteModalOpenHandler: (sub: TSubscription) => () => void,
  totalActiveSubscriptions: number
) => [
  {
    key: 'page_name',
    name: `Issue (${totalActiveSubscriptions})`,
    width: '35%',
    formatFn: (tagName: string, subscription: TSubscription) => (
      <>
        {getMonthlyTagName(tagName)}
        {subscription.state === 'active' ? (
          <RecurringCancelLink
            $color={theme.shadows.black(0.5)}
            onClick={getDeleteModalOpenHandler(subscription)}
          >
            Cancel
          </RecurringCancelLink>
        ) : (
          <RecurringCancelled $color={theme.shadows.black(0.5)}>Canceled</RecurringCancelled>
        )}
      </>
    ),
  },
  {
    key: 'next_transaction_date',
    name: 'Next Payment',
    formatFn: getFormattedDate,
    className: 'align-right',
  },
  {
    key: 'last_transaction_date',
    name: 'Last Payment',
    formatFn: getFormattedDate,
    className: 'align-right',
  },
  {
    key: 'amount',
    name: 'Donation Amount',
    formatFn: (amount: number) => formatNumberAsCurrency(amount, { trailingZeroDisplay: 'auto' }),
    width: '25%',
  },
];

export const getColumnsMonthlyMobile = (
  getDeleteModalOpenHandler: (sub: TSubscription) => () => void,
  totalActiveSubscriptions: number
) => [
  {
    key: 'tag_name',
    name: `Issue (${totalActiveSubscriptions})`,
    width: '50%',
    formatFn: (tagName: string, subscription: TSubscription) => (
      <>
        <div>
          <BodySmall $color="inherit">{getMonthlyTagName(tagName)}</BodySmall>
        </div>
        <Spacing $size={3} />
        <div>
          <UtilityText $color={subscription.state === 'active' ? theme.colors.black : 'inherit'}>
            Next Payment: {getFormattedDate(subscription.next_transaction_date)}
          </UtilityText>
        </div>
        <div>
          <UtilityText $color={subscription.state === 'active' ? theme.colors.black : 'inherit'}>
            Last Payment: {getFormattedDate(subscription.last_transaction_date)}
          </UtilityText>
        </div>
      </>
    ),
  },
  {
    key: 'amount',
    name: 'Donation Amount',
    formatFn: (amount: number, subscription: TSubscription) => (
      <RecurringAmountMobileContainer $justifyContent="space-between">
        <div>
          <BodySmall $color="inherit">
            {formatNumberAsCurrency(amount, { trailingZeroDisplay: 'auto' })}
          </BodySmall>
        </div>
        <div>
          {subscription.state === 'active' ? (
            <RecurringCancelLink
              $color={theme.shadows.black(0.5)}
              onClick={getDeleteModalOpenHandler(subscription)}
            >
              <UtilityText>Cancel</UtilityText>
            </RecurringCancelLink>
          ) : (
            <RecurringCancelled $color="inherit">
              <UtilityText $color="inherit">Canceled</UtilityText>
            </RecurringCancelled>
          )}
        </div>
      </RecurringAmountMobileContainer>
    ),
  },
];
